import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CircleNotch, MagnifyingGlass } from "@phosphor-icons/react";
import useDrawsUpdated from "../hooks/useDrawsUpdated";
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = process.env.REACT_APP_BACKEND_URL;

// Tailwind colour classes per tier (keyed to backend tier_colors)
const TIER_STYLE = {
  // WHO
  "Workhorse": "bg-emerald-600 text-white",
  "Ordinary":  "bg-amber-500 text-white",
  "Outlier":   "bg-rose-600 text-white",
  // ABL
  "Above":    "bg-red-500 text-white",
  "Balanced": "bg-amber-500 text-white",
  "Low":      "bg-blue-500 text-white",
  // SMC
  "Sizzling": "bg-red-500 text-white",
  "Mild":     "bg-amber-500 text-white",
  "Cold":     "bg-blue-500 text-white",
  // DRO
  "Recent":   "bg-emerald-500 text-white",
  "Due":      "bg-gray-400 text-white",
  "Overdue":  "bg-rose-600 text-white",
  // L6
  "Appeared": "bg-emerald-500 text-white",
  "Absent":   "bg-gray-300 text-gray-700",
  // Prime
  "Prime":     "bg-violet-500 text-white",
  "Non-Prime": "bg-gray-200 text-gray-600",
  // DNW
  "Dominant": "bg-red-500 text-white",
  "Neutral":  "bg-amber-500 text-white",
  "Weak":     "bg-blue-500 text-white",
  // DGT (Digit Likelihood)
  "Strong":   "bg-fuchsia-600 text-white",
  "Moderate": "bg-amber-500 text-white",
  "Avoid":    "bg-rose-700 text-white",
};

const VERDICT_STYLE = {
  "STRONG":   "bg-emerald-600 text-white",
  "LEANING+": "bg-emerald-400 text-white",
  "NEUTRAL":  "bg-gray-300 text-gray-700",
  "MIXED":    "bg-amber-400 text-white",
  "LEANING-": "bg-rose-400 text-white",
  "WEAK":     "bg-rose-700 text-white",
};

export default function NumberTierConsensus() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [verdictFilter, setVerdictFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("consensus");

  const fetchData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true); else setRefreshing(true);
      const res = await axios.get(`${API}/api/consensus/tier-vector`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(false); }, [fetchData]);
  useDrawsUpdated(() => fetchData(true));

  const displayRows = useMemo(() => {
    if (!data?.rows) return [];
    let rows = [...data.rows];
    if (search.trim()) {
      const q = search.trim();
      rows = rows.filter(r => String(r.number).includes(q));
    }
    if (verdictFilter !== "ALL") {
      rows = rows.filter(r => r.verdict === verdictFilter);
    }
    if (sortBy === "number") rows.sort((a, b) => a.number - b.number);
    else if (sortBy === "agreement") rows.sort((a, b) => (b.agreement_pct - a.agreement_pct) || (b.consensus_score - a.consensus_score));
    else rows.sort((a, b) => (b.consensus_score - a.consensus_score) || (b.agreement_pct - a.agreement_pct) || (a.number - b.number));
    return rows;
  }, [data, search, verdictFilter, sortBy]);

  const verdictBreakdown = useMemo(() => {
    if (!data?.rows) return {};
    const acc = {};
    for (const r of data.rows) {
      if (!acc[r.verdict]) acc[r.verdict] = [];
      acc[r.verdict].push(r);
    }
    // Sort each bucket by consensus score desc, then number asc
    for (const v of Object.keys(acc)) {
      acc[v].sort((a, b) => (b.consensus_score - a.consensus_score) || (a.number - b.number));
    }
    return acc;
  }, [data]);

  const verdictCounts = useMemo(() => {
    if (!data?.rows) return {};
    return data.rows.reduce((acc, r) => { acc[r.verdict] = (acc[r.verdict] || 0) + 1; return acc; }, {});
  }, [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (error || !data || data.error) return <div className="min-h-screen flex items-center justify-center text-red-600">Failed to load: {error || data?.error || 'No data'}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-btn"><ArrowLeft size={20} /></button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              W56: Number Tier Consensus (GYR)
              <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-normal">
                <span className={`w-1.5 h-1.5 rounded-full bg-white ${refreshing ? '' : 'animate-pulse'}`} /> {refreshing ? 'Refreshing…' : 'Auto-updating'}
              </span>
            </h1>
            <p className="text-xs text-gray-500">
              Unified view across 6 dimensions (renamed to avoid HML collision) · Latest draw #{data.latest_draw?.draw_number} ({data.latest_draw?.draw_date})
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 py-6 space-y-6">


        <WindowNarrative windowId="W56" {...NARRATIVES.W56} className="mb-5" />
        {/* Dimensions legend */}
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="dim-legend">
          <div className="text-sm font-bold text-gray-800 mb-2">📚 Renamed Triples — Each Window Now Speaks Its Own Language</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.dimensions.map(d => {
              const isHighPriority = (d.weight || 1) >= 2;
              return (
              <div key={d.key} className={`border rounded-lg p-3 ${isHighPriority ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-300' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex w-12 h-6 rounded font-mono font-bold text-xs items-center justify-center ${isHighPriority ? 'bg-emerald-700 text-white' : 'bg-[#002FA7] text-white'}`}>{d.key}</span>
                  <span className="text-sm font-bold text-gray-800">{d.full_name}</span>
                  {isHighPriority && <span className="text-[10px] font-bold bg-emerald-700 text-white px-1.5 py-0.5 rounded">×{d.weight}</span>}
                  {(d.weight || 1) === 0 && <span className="text-[10px] font-bold bg-gray-400 text-white px-1.5 py-0.5 rounded">info</span>}
                </div>
                <div className="text-[11px] text-gray-500 mt-1">Source: <span className="font-bold">{d.source}</span></div>
                <div className="text-[11px] text-gray-600 mt-0.5">{d.description}</div>
              </div>
              );
            })}
          </div>
          <div className="mt-3 text-[11px] text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
            <span className="font-bold text-amber-800">🎲 Gambler's Fallacy polarity:</span> "Overdue" is NOT treated as bullish. Recent = +1 (momentum), Due = 0, Overdue = −1.
          </div>
        </div>

        {/* Verdict summary bar */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="verdict-summary">
          {["STRONG", "LEANING+", "NEUTRAL", "MIXED", "LEANING-", "WEAK"].map(v => (
            <button
              key={v}
              onClick={() => setVerdictFilter(verdictFilter === v ? "ALL" : v)}
              className={`border rounded-lg p-3 text-left transition ${verdictFilter === v ? 'ring-2 ring-[#002FA7] bg-blue-50' : 'bg-white hover:bg-gray-50'}`}
              data-testid={`verdict-card-${v}`}
            >
              <div className="flex items-center justify-between">
                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${VERDICT_STYLE[v]}`}>{v}</span>
                <span className="text-xs text-gray-500">{verdictCounts[v] || 0}</span>
              </div>
              <div className="text-2xl font-mono font-bold text-gray-800 mt-1">{verdictCounts[v] || 0}</div>
              <div className="text-[10px] text-gray-500">numbers</div>
            </button>
          ))}
        </div>

        {/* Verdict Breakdown — actual numbers in each category */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="verdict-breakdown">
          <div className="bg-gradient-to-r from-[#002FA7] to-indigo-700 text-white px-5 py-3">
            <h3 className="font-bold text-base">📊 Verdict Breakdown — Which numbers landed where?</h3>
            <p className="text-[11px] text-white/80">Each chip shows Number + Consensus Score. Click a number to filter the table below. Sorted by score within each verdict.</p>
          </div>
          <div className="divide-y divide-gray-100">
            {["STRONG", "LEANING+", "NEUTRAL", "MIXED", "LEANING-", "WEAK"].map(v => {
              const list = verdictBreakdown[v] || [];
              if (list.length === 0) return (
                <div key={v} className="px-5 py-3 flex items-center gap-3 bg-gray-50" data-testid={`vb-row-${v}`}>
                  <span className={`inline-block px-3 py-1 rounded text-[11px] font-bold min-w-[90px] text-center ${VERDICT_STYLE[v]}`}>{v}</span>
                  <span className="text-xs text-gray-400 italic">— none —</span>
                </div>
              );
              return (
                <div key={v} className="px-5 py-3 flex items-start gap-3" data-testid={`vb-row-${v}`}>
                  <div className="flex flex-col items-center min-w-[90px] pt-0.5">
                    <span className={`inline-block px-3 py-1 rounded text-[11px] font-bold ${VERDICT_STYLE[v]}`}>{v}</span>
                    <span className="text-[10px] text-gray-500 mt-0.5 font-bold">{list.length} number{list.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {list.map(r => (
                      <button
                        key={r.number}
                        onClick={() => setSearch(String(r.number))}
                        title={`#${r.number} · Consensus ${r.consensus_score >= 0 ? '+' : ''}${r.consensus_score} · Agreement ${r.agreement_pct}%`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-bold font-mono transition hover:scale-105 ${
                          r.consensus_score >= 4 ? 'bg-emerald-600 text-white border-emerald-700' :
                          r.consensus_score >= 1 ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                          r.consensus_score === 0 ? 'bg-gray-100 text-gray-700 border-gray-300' :
                          r.consensus_score >= -3 ? 'bg-rose-100 text-rose-800 border-rose-300' :
                          'bg-rose-600 text-white border-rose-700'
                        }`}
                        data-testid={`vb-num-${r.number}`}
                      >
                        <span className="inline-flex w-5 h-5 rounded-full bg-white/30 items-center justify-center">{r.number}</span>
                        <span className="text-[10px] opacity-90">{r.consensus_score >= 0 ? `+${r.consensus_score}` : r.consensus_score}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-5 py-2 bg-gray-50 border-t text-[10px] text-gray-500">
            💡 Tip: Click any number chip to auto-filter the table below. STRONG + LEANING+ = <span className="font-bold">{(verdictCounts.STRONG || 0) + (verdictCounts["LEANING+"] || 0)}</span> playable candidates. WEAK + LEANING− = <span className="font-bold">{(verdictCounts.WEAK || 0) + (verdictCounts["LEANING-"] || 0)}</span> avoid-list.
          </div>
        </div>

        {/* 3-Group Summary — GREEN/YELLOW/RED */}
        {data.groups && (() => {
          const g = data.groups;
          const GROUP_META = {
            GREEN:  { bg: "bg-emerald-500", text: "text-white", light: "bg-emerald-50 border-emerald-300", tag: "bg-emerald-100 text-emerald-800", label: "GREEN · Play", sub: "STRONG (score ≥ +4)" },
            YELLOW: { bg: "bg-amber-500",   text: "text-white", light: "bg-amber-50 border-amber-300",   tag: "bg-amber-100 text-amber-800",   label: "YELLOW · Caution", sub: "LEANING+ (score +1 to +3)" },
            RED:    { bg: "bg-rose-600",    text: "text-white", light: "bg-rose-50 border-rose-300",     tag: "bg-rose-100 text-rose-800",     label: "RED · Skip", sub: "NEUTRAL / MIXED / LEANING- / WEAK (score ≤ 0)" },
          };
          return (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="three-group">
              <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white px-5 py-3">
                <h3 className="font-bold text-base">🚦 Universal 3-Group View (Traffic-Light Consensus)</h3>
                <p className="text-[11px] text-white/80">
                  Collapses the 6 verdicts into 3 actionable buckets. Historical backtest below validates whether the consensus actually aligns with draw reality.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-b border-gray-200">
                {["GREEN", "YELLOW", "RED"].map(gk => {
                  const m = GROUP_META[gk];
                  const nums = Object.entries(g.number_group).filter(([,v]) => v === gk).map(([n]) => parseInt(n)).sort((a,b) => a-b);
                  return (
                    <div key={gk} className={`p-4 border-r last:border-r-0 ${m.light}`} data-testid={`group-card-${gk}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex w-8 h-8 rounded-full ${m.bg} ${m.text} items-center justify-center font-bold`}>{gk[0]}</span>
                        <div>
                          <div className="font-bold text-sm">{m.label}</div>
                          <div className="text-[10px] text-gray-500">{m.sub}</div>
                        </div>
                        <span className={`ml-auto inline-block px-2 py-0.5 rounded text-[11px] font-bold ${m.tag}`}>{g.sizes[gk]} nums</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {nums.map(n => (
                          <button key={n} onClick={() => setSearch(String(n))} className={`inline-flex w-7 h-7 rounded-full ${m.bg} ${m.text} items-center justify-center font-mono font-bold text-[11px] hover:scale-110 transition`} data-testid={`g-${gk}-n-${n}`}>{n}</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Expected vs Actual mix (backtest) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-b border-gray-200">
                <div className="p-4 border-r border-gray-200" data-testid="mix-naive">
                  <div className="text-[11px] font-bold text-gray-500 uppercase mb-2">🎲 Naive Expected Mix per Draw</div>
                  <div className="text-[10px] text-gray-500 mb-2">= 5 × (group size ÷ 50) — if draws were purely random</div>
                  <div className="flex gap-2">
                    {["GREEN","YELLOW","RED"].map(gk => (
                      <div key={gk} className={`flex-1 rounded px-2 py-2 text-center ${GROUP_META[gk].tag}`}>
                        <div className="text-[10px] font-bold">{gk}</div>
                        <div className="text-xl font-mono font-bold">{g.naive_expected_mix[gk]}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 bg-slate-50" data-testid="mix-actual">
                  <div className="text-[11px] font-bold text-gray-700 uppercase mb-2">📈 Actual Avg Mix — Last {g.history_draws_used} Draws</div>
                  <div className="text-[10px] text-gray-500 mb-2">What the consensus-tier map predicts vs what actually landed</div>
                  <div className="flex gap-2">
                    {["GREEN","YELLOW","RED"].map(gk => {
                      const actual = g.avg_draw_mix[gk];
                      const naive = g.naive_expected_mix[gk];
                      const delta = actual - naive;
                      return (
                        <div key={gk} className={`flex-1 rounded px-2 py-2 text-center ${GROUP_META[gk].tag} relative`}>
                          <div className="text-[10px] font-bold">{gk}</div>
                          <div className="text-xl font-mono font-bold">{actual}</div>
                          <div className={`text-[10px] font-bold ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-rose-700' : 'text-gray-500'}`}>
                            {delta > 0 ? '+' : ''}{delta.toFixed(2)} vs random
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="text-[10px] text-gray-600 mt-2 italic">
                    {g.avg_draw_mix.GREEN > g.naive_expected_mix.GREEN + 0.3
                      ? `✅ Consensus system works: GREEN over-indexes by +${(g.avg_draw_mix.GREEN - g.naive_expected_mix.GREEN).toFixed(2)} balls per draw.`
                      : "⚠️ Consensus alignment is weak — investigate."}
                  </div>
                </div>
              </div>

              {/* Top patterns */}
              <div className="p-4 border-b border-gray-200" data-testid="top-patterns">
                <div className="text-[11px] font-bold text-gray-500 uppercase mb-2">🏆 Most Common Compositions — Last {g.history_draws_used} Draws</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {g.top_patterns.slice(0, 10).map((tp, i) => {
                    const parts = tp.pattern.split("-");
                    return (
                      <div key={tp.pattern} className={`border rounded p-2 text-center ${i === 0 ? 'ring-2 ring-slate-800 bg-slate-100' : 'bg-white'}`}>
                        <div className="flex justify-center gap-1 mb-1">
                          {parts.map((p, pi) => {
                            const gk = p[0] === 'G' ? 'GREEN' : p[0] === 'Y' ? 'YELLOW' : 'RED';
                            return (
                              <span key={pi} className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${GROUP_META[gk].tag}`}>{p}</span>
                            );
                          })}
                        </div>
                        <div className="font-bold text-base">{tp.count}×</div>
                        <div className="text-[10px] text-gray-500">{tp.pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Per-draw table */}
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto" data-testid="per-draw-table">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-600 font-bold">Draw</th>
                      {[1,2,3,4,5].map(i => <th key={i} className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Ball {i}</th>)}
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Composition</th>
                      <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Pattern</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...g.draw_compositions].reverse().map((dc, idx) => (
                      <tr key={dc.draw_number} className={idx === 0 ? 'bg-emerald-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 border-b">
                          <div className="font-bold text-gray-800 flex items-center gap-1.5">#{dc.draw_number}{idx === 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-500 text-white">LATEST</span>}</div>
                          <div className="text-[10px] text-gray-400">{dc.draw_date}</div>
                        </td>
                        {dc.ball_details.map((bd, bi) => {
                          const m = GROUP_META[bd.group];
                          return (
                            <td key={bi} className="px-3 py-2 border-b text-center">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className={`inline-flex w-9 h-9 rounded-full ${m.bg} ${m.text} items-center justify-center font-mono font-bold text-sm`}>{bd.number}</span>
                                <span className={`text-[9px] font-bold px-1.5 rounded ${m.tag}`}>{bd.group[0]} {bd.score >= 0 ? '+' : ''}{bd.score}</span>
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 border-b text-center">
                          <div className="flex justify-center gap-1">
                            {["GREEN","YELLOW","RED"].map(gk => (
                              <span key={gk} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${GROUP_META[gk].tag}`}>{gk[0]}:{dc.mix[gk]}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 border-b text-center">
                          <span className="font-mono font-bold text-sm text-gray-800">{dc.pattern}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Controls */}
        <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-3" data-testid="controls">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search number…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 text-sm border-0 outline-none bg-transparent"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-bold">Sort:</span>
            {[["consensus", "Consensus ↓"], ["agreement", "Agreement ↓"], ["number", "Number ↑"]].map(([k, l]) => (
              <button key={k} onClick={() => setSortBy(k)} className={`px-2 py-1 text-xs rounded border ${sortBy === k ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid={`sort-${k}`}>
                {l}
              </button>
            ))}
          </div>
          {verdictFilter !== "ALL" && (
            <button onClick={() => setVerdictFilter("ALL")} className="text-xs text-gray-500 underline" data-testid="clear-filter">Clear filter</button>
          )}
          <div className="text-xs text-gray-500">{displayRows.length} / 50 numbers</div>
        </div>

        {/* Main matrix table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="matrix-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-[10px] uppercase text-gray-600 font-bold">#</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Number</th>
                  {data.dimensions.map(d => {
                    const isHighPriority = (d.weight || 1) >= 2;
                    return (
                      <th key={d.key} className={`px-3 py-2 text-center text-[10px] uppercase font-bold ${isHighPriority ? 'bg-emerald-100 text-emerald-800' : 'text-gray-600'}`} title={d.full_name}>
                        {d.key}
                        {isHighPriority && <span className="block text-[8px] text-emerald-700 font-bold normal-case">×{d.weight} weight</span>}
                      </th>
                    );
                  })}
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Score</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Agreement</th>
                  <th className="px-3 py-2 text-center text-[10px] uppercase text-gray-600 font-bold">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r, idx) => (
                  <tr key={r.number} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition`} data-testid={`row-${r.number}`}>
                    <td className="px-3 py-2 border-b text-gray-400 font-mono text-[10px]">{idx + 1}</td>
                    <td className="px-3 py-2 border-b text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-bold text-sm ${r.verdict === 'STRONG' ? 'bg-emerald-600 text-white' : r.verdict === 'WEAK' ? 'bg-rose-600 text-white' : 'bg-[#002FA7]/10 text-[#002FA7]'}`}>{r.number}</span>
                        {r.is_prime && <span className="text-[9px] font-bold text-violet-600" title="Prime">P</span>}
                      </div>
                    </td>
                    {data.dimensions.map(dim => {
                      const t = r.tiers[dim.key];
                      const isHighPriority = (dim.weight || 1) >= 2;
                      return (
                        <td key={dim.key} className={`px-3 py-2 border-b text-center ${isHighPriority ? 'bg-emerald-50/50' : ''}`} title={t.unit}>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${TIER_STYLE[t.label] || 'bg-gray-200 text-gray-600'}`}>
                              {t.label}
                            </span>
                            <span className="text-[9px] text-gray-500">{t.unit}</span>
                            <span className={`text-[10px] font-mono font-bold ${t.score > 0 ? 'text-emerald-600' : t.score < 0 ? 'text-rose-600' : 'text-gray-400'}`}>
                              {t.score > 0 ? `+${t.score}` : t.score}
                            </span>
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 border-b text-center">
                      <span className={`inline-flex w-10 h-10 rounded-full items-center justify-center font-mono font-bold text-base ${r.consensus_score >= 3 ? 'bg-emerald-600 text-white' : r.consensus_score >= 1 ? 'bg-emerald-100 text-emerald-700' : r.consensus_score === 0 ? 'bg-gray-100 text-gray-700' : r.consensus_score >= -2 ? 'bg-rose-100 text-rose-700' : 'bg-rose-600 text-white'}`}>
                        {r.consensus_score > 0 ? `+${r.consensus_score}` : r.consensus_score}
                      </span>
                    </td>
                    <td className="px-3 py-2 border-b text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-mono font-bold text-gray-700">{r.agreement_pct}%</span>
                        <span className="text-[9px] text-gray-500">{r.positive_count}▲ {r.zero_count}● {r.negative_count}▼</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 border-b text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${VERDICT_STYLE[r.verdict]}`}>{r.verdict}</span>
                      {r.is_conflict && <div className="text-[9px] text-amber-600 font-bold mt-0.5">⚠ mixed</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="text-[11px] text-gray-500 bg-white border border-gray-200 rounded-lg p-3">
          <div className="font-bold text-gray-700 mb-1">How to read this view</div>
          <ul className="list-disc ml-4 space-y-0.5">
            <li><span className="font-bold">WHO has ×2 weight</span> (range ±2) because it encodes 22 years of historical position-coverage — the strongest empirical signal.</li>
            <li><span className="font-bold">Consensus Score</span> = WHO(±2) + ABL(±1) + SMC(±1) + DRO(±1) + L6(0/+1) + DNW(±1). Prime is 0 (informational only). Overall range ≈ −6 … +7.</li>
            <li><span className="font-bold">Agreement %</span> = how strongly the non-zero signals align with the dominant sign.</li>
            <li><span className="font-bold">MIXED</span> verdict means conflicting signals (≥2 positive AND ≥2 negative) — worth investigating, not blindly trusting.</li>
            <li>Use this page to validate your Multi-Window Projection / Prediction Maker picks: a STRONG number is supported by multiple independent lenses.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
